<template>
  <div class="admin-page">
    <header class="header-section">
      <h1>Administration</h1>
    </header>

    <div class="admin-sections">
      <!-- Business Settings Section -->
      <section class="admin-section">
        <div class="section-header" @click="toggleSection('businessSettings')">
          <div class="header-content">
            <i class="fas fa-store"></i>
            <h2>Forretningsindstillinger</h2>
          </div>
          <img 
            src="@/assets/images/icons/chevron.svg" 
            class="chevron-icon" 
            :class="{ 'is-expanded': expandedSections.businessSettings }"
            alt="Expand"
          />
        </div>
        
        <div class="section-content" :class="{ 'is-expanded': expandedSections.businessSettings }">

          <!-- Salary Targets -->
          <section class="salary-targets">
            <div class="section-header" @click="toggleSection('salaryTargets')">
              <div class="header-content">
                <h3>Lønprocentmål</h3>
              </div>
              <img 
                src="@/assets/images/icons/chevron.svg" 
                class="chevron-icon" 
                :class="{ 'is-expanded': expandedSections.salaryTargets }"
                alt="Expand"
              />
            </div>
            <div class="section-content" :class="{ 'is-expanded': expandedSections.salaryTargets }">
              <div v-for="business in userBusinesses" 
                   :key="business.id" 
                   class="business-targets"
              >
                <div class="business-header" @click="toggleBusiness(business.id)">
                  <div class="header-content">
                    <h3>{{ business.businessData?.businessName }}</h3>
                    <div class="summary">
                      <span>Gennemsnit: {{ getAverageSalaryTarget(business) }}%</span>
                    </div>
                  </div>
                  <img 
                    src="@/assets/images/icons/chevron.svg" 
                    class="chevron-icon" 
                    :class="{ 'is-expanded': expandedBusinesses[business.id] }"
                    alt="Expand"
                  />
                </div>
                
                <div class="business-content" 
                     :class="{ 'is-expanded': expandedBusinesses[business.id] }"
                >
                  <div class="months-grid">
                    <div v-for="month in months" 
                         :key="month.value" 
                         class="month-input"
                    >
                      <label :for="`${business.id}-${month.value}`">{{ month.label }}</label>
                      <div class="input-group">
                        <input
                          type="number"
                          :id="`${business.id}-${month.value}`"
                          :value="salaryInputs[business.id]?.[month.value] || business.businessData?.salaryTarget?.[month.value] || 0"
                          @input="updateSalaryInput(business.id, month.value, $event)"
                          min="0"
                          max="100"
                          step="0.5"
                          :disabled="!isOwner"
                        >
                        <span class="percentage">%</span>
                      </div>
                    </div>
                  </div>
                  
                  <button v-if="isOwner" @click="saveSalaryTargets(business.id)" class="save-button">
                    Gem ændringer
                  </button>
                </div>
              </div>
            </div>
          </section>

          <!-- Closed Days -->
          <section class="closed-days">
            <div class="section-header" @click="toggleSection('closedDays')">
              <div class="header-content">
                <h3>Lukkedage</h3>
              </div>
              <img 
                src="@/assets/images/icons/chevron.svg" 
                class="chevron-icon" 
                :class="{ 'is-expanded': expandedSections.closedDays }"
                alt="Expand"
              />
            </div>
            <div class="section-content" :class="{ 'is-expanded': expandedSections.closedDays }">
              <div v-for="business in userBusinesses" 
                   :key="business.id" 
                   class="business-targets"
              >
                <div class="business-header" @click="toggleClosedDays(business.id)">
                  <div class="header-content">
                    <h3>{{ business.businessData?.businessName }}</h3>
                    <div class="summary">
                      <span>Antal lukkedage: {{ getClosedDaysCount(business) }}</span>
                    </div>
                  </div>
                  <img 
                    src="@/assets/images/icons/chevron.svg" 
                    class="chevron-icon" 
                    :class="{ 'is-expanded': expandedClosedDays[business.id] }"
                    alt="Expand"
                  />
                </div>

                <div class="business-content" 
                     :class="{ 'is-expanded': expandedClosedDays[business.id] }"
                >
                  <div class="quick-select-options">
                    <h4>Mærkedage</h4>
                    <div class="common-dates">
                      <button 
                        v-for="(date, index) in commonClosedDates" 
                        :key="index"
                        class="quick-select-btn"
                        :class="{ 'selected': isCommonDateSelected(business.id, date.value) }"
                        @click="toggleCommonDate(business.id, date.value)"
                      >
                        {{ date.label }}
                      </button>
                    </div>
                  </div>

                  <div class="weekly-closures">
                    <h4>Ugedage der er lukket</h4>
                    <div class="weekday-buttons">
                      <button 
                        v-for="day in weekDays" 
                        :key="day.value"
                        class="quick-select-btn"
                        :class="{ 'selected': isWeekdaySelected(business.id, day.value) }"
                        @click="toggleWeekday(business.id, day.value)"
                      >
                        {{ day.label }}
                      </button>
                    </div>
                  </div>

                  <div class="closed-days-picker">
                    <h4>Vælg specifikke datoer</h4>
                    <v-date-picker
                      v-model="closedDays[business.id]"
                      mode="multiple"
                      :min-date="new Date()"
                      :attributes="calendarAttributes"
                    />
                  </div>
                  
                  <button @click="saveClosedDays(business.id)" class="save-button">
                    Gem lukkedage
                  </button>
                </div>
              </div>
            </div>
          </section>
          <!-- Takeaway Order Types -->
          <section class="takeaway-types">
            <div class="section-header" @click="toggleSection('takeawayTypes')">
              <div class="header-content">
                <h3>Takeaway</h3>
              </div>
              <img
                src="@/assets/images/icons/chevron.svg"
                class="chevron-icon"
                :class="{ 'is-expanded': expandedSections.takeawayTypes }"
                alt="Expand"
              />
            </div>
            <div class="section-content" :class="{ 'is-expanded': expandedSections.takeawayTypes }">
              <div
                v-for="business in userBusinesses"
                :key="business.id"
                class="business-targets"
              >
                <div class="business-header" @click="toggleTakeaway(business.id)">
                  <div class="header-content">
                    <h3>{{ business.businessData?.businessName }}</h3>
                    <div class="summary">
                      <span>Ordretyper: {{ (business.businessData?.takeawayOrderTypes || []).length }}</span>
                    </div>
                  </div>
                  <img
                    src="@/assets/images/icons/chevron.svg"
                    class="chevron-icon"
                    :class="{ 'is-expanded': expandedTakeaway[business.id] }"
                    alt="Expand"
                  />
                </div>

                <div
                  class="business-content"
                  :class="{ 'is-expanded': expandedTakeaway[business.id] }"
                >
                  <p class="takeaway-helper">
                    Indtast det præcise salgsnavn fra kassesystemet (f.eks. "TA", "Take Away", "ToGo"). En restaurant uden ordretyper behandles som udelukkende dine-in.
                  </p>

                  <div class="order-type-chips">
                    <span
                      v-for="(type, index) in (takeawayInputs[business.id] || [])"
                      :key="index"
                      class="chip"
                    >
                      {{ type }}
                      <button
                        v-if="isOwner"
                        class="chip-remove"
                        @click="removeOrderType(business.id, index)"
                        aria-label="Fjern"
                      >×</button>
                    </span>
                    <span v-if="!(takeawayInputs[business.id] || []).length" class="chip chip--empty">
                      Ingen ordretyper — kun dine-in
                    </span>
                  </div>

                  <div v-if="isOwner" class="add-order-type">
                    <input
                      type="text"
                      :id="`takeaway-input-${business.id}`"
                      v-model="newOrderTypeInput[business.id]"
                      placeholder="Ny ordretypenavn…"
                      @keyup.enter="addOrderType(business.id)"
                    />
                    <button class="add-btn" @click="addOrderType(business.id)">Tilføj</button>
                  </div>

                  <button v-if="isOwner" @click="saveTakeawayOrderTypes(business.id)" class="save-button">
                    Gem ordretyper
                  </button>
                </div>
              </div>
            </div>
          </section>

          <!-- Busyness Thresholds -->
          <section class="busyness-thresholds">
            <div class="section-header" @click="toggleSection('busynessThresholds')">
              <div class="header-content">
                <h3>Travlhedstærskler</h3>
              </div>
              <img
                src="@/assets/images/icons/chevron.svg"
                class="chevron-icon"
                :class="{ 'is-expanded': expandedSections.busynessThresholds }"
                alt="Expand"
              />
            </div>
            <div class="section-content" :class="{ 'is-expanded': expandedSections.busynessThresholds }">
              <div
                v-for="business in userBusinesses"
                :key="business.id"
                class="business-targets"
              >
                <div class="business-header" @click="toggleThresholds(business.id)">
                  <div class="header-content">
                    <h3>{{ business.businessData?.businessName }}</h3>
                  </div>
                  <img
                    src="@/assets/images/icons/chevron.svg"
                    class="chevron-icon"
                    :class="{ 'is-expanded': expandedThresholds[business.id] }"
                    alt="Expand"
                  />
                </div>

                <div class="business-content" :class="{ 'is-expanded': expandedThresholds[business.id] }">
                  <div class="thresholds-grid">
                    <div v-for="field in thresholdFields" :key="field.key" class="threshold-input">
                      <label :for="`${business.id}-${field.key}`">{{ field.label }}</label>
                      <div class="input-group">
                        <input
                          type="number"
                          :id="`${business.id}-${field.key}`"
                          :value="thresholdInputs[business.id]?.[field.key] ?? (business.businessData?.busynessThresholds?.[field.key] ?? field.default)"
                          @input="updateThresholdInput(business.id, field.key, $event)"
                          :disabled="!isOwner"
                          :step="field.key === 'minimumSales' ? 50 : 5"
                        />
                        <span class="percentage">{{ field.key === 'minimumSales' ? 'kr' : '%' }}</span>
                      </div>
                    </div>
                  </div>

                  <button v-if="isOwner" @click="saveThresholds(business.id)" class="save-button">
                    Gem tærskler
                  </button>
                </div>
              </div>
            </div>
          </section>

        </div>
      </section>

      <!-- User Management Section -->
      <section class="admin-section" v-if="isAuthorized">
        <div class="section-header" @click="toggleSection('userManagement')">
          <div class="header-content">
            <i class="fas fa-users-cog"></i>
            <h2>Brugeradministration</h2>
          </div>
          <img 
            src="@/assets/images/icons/chevron.svg" 
            class="chevron-icon" 
            :class="{ 'is-expanded': expandedSections.userManagement }"
            alt="Expand"
          />
        </div>
        
        <div class="section-content" :class="{ 'is-expanded': expandedSections.userManagement }">
          <div class="user-management-sections">
            <div class="add-user-section">
              <div class="section-header" @click="toggleSection('addUser')">
                <div class="header-content">
                  <i class="fas fa-user-plus"></i>
                  <h3>Tilføj ny bruger</h3>
                </div>
                <img 
                  src="@/assets/images/icons/chevron.svg" 
                  class="chevron-icon" 
                  :class="{ 'is-expanded': expandedSections.addUser }"
                  alt="Expand"
                />
              </div>
              <div class="section-content" :class="{ 'is-expanded': expandedSections.addUser }">
                <UserManagement :organizations="organizations" class="user-management-section" />
              </div>
            </div>
            
            <div class="manage-users-section">
              <ManageUsers :organizations="organizations" />
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import UserManagement from '@/app/components/admin/UserManagement.vue';
import ManageUsers from '@/app/components/admin/ManageUsers.vue';

export default {
  name: 'AdminPage',
  components: {
    UserManagement,
    ManageUsers
  },
  setup() {
    const store = useStore();
    const salaryInputs = ref({});
    const expandedBusinesses = ref({});
    const expandedClosedDays = ref({});
    const expandedTakeaway = ref({});
    const closedDays = ref({});
    const takeawayInputs = ref({});
    const newOrderTypeInput = ref({});
    const expandedThresholds = ref({});
    const thresholdInputs = ref({});
    const expandedSections = ref({
      businessSettings: false,
      userManagement: false,
      addUser: false,
      salaryTargets: false,
      closedDays: false,
      takeawayTypes: false,
      busynessThresholds: false
    });

    const thresholdFields = [
      { key: 'veryQuiet', label: 'Meget stille under %', default: -35 },
      { key: 'quiet',     label: 'Lidt stille under %',  default: -10 },
      { key: 'busy',      label: 'Travlt over %',         default: 10 },
      { key: 'veryBusy',  label: 'Meget travlt over %',   default: 35 },
      { key: 'minimumSales', label: 'Minimum omsætning',  default: 500 },
    ];

    const months = [
      { value: '01', label: 'Januar' },
      { value: '02', label: 'Februar' },
      { value: '03', label: 'Marts' },
      { value: '04', label: 'April' },
      { value: '05', label: 'Maj' },
      { value: '06', label: 'Juni' },
      { value: '07', label: 'Juli' },
      { value: '08', label: 'August' },
      { value: '09', label: 'September' },
      { value: '10', label: 'Oktober' },
      { value: '11', label: 'November' },
      { value: '12', label: 'December' }
    ];

    const userBusinesses = computed(() => {
      const businesses = [];
      store.state.organizations.forEach(org => {
        org.businesses.forEach(business => {
          businesses.push({
            id: business.name,
            businessData: business.data,
          });
        });
      });
      return businesses;
    });

    const updateSalaryInput = (businessId, month, event) => {
      if (!salaryInputs.value[businessId]) {
        salaryInputs.value[businessId] = {};
      }
      salaryInputs.value[businessId][month] = Number(event.target.value);
    };

    onMounted(() => {
      userBusinesses.value.forEach(business => {
        if (!salaryInputs.value[business.id]) {
          salaryInputs.value[business.id] = {};
        }
        months.forEach(month => {
          salaryInputs.value[business.id][month.value] = 
            business.businessData?.salaryTarget?.[month.value] || 0;
        });

        closedDays.value[business.id] = {
          specificDates: business.businessData?.closedDays?.specificDates || [],
          commonDates: business.businessData?.closedDays?.commonDates || [],
          weeklyClosures: business.businessData?.closedDays?.weeklyClosures || []
        };

        takeawayInputs.value[business.id] = [...(business.businessData?.takeawayOrderTypes || [])];
        newOrderTypeInput.value[business.id] = '';

        thresholdInputs.value[business.id] = {};
        thresholdFields.forEach(field => {
          thresholdInputs.value[business.id][field.key] =
            business.businessData?.busynessThresholds?.[field.key] ?? field.default;
        });
      });
    });

    const saveSalaryTargets = async (businessId) => {
      const businessTargets = salaryInputs.value[businessId];
      for (const [month, percentage] of Object.entries(businessTargets)) {
        await store.dispatch('updateSalaryTarget', {
          businessId,
          month,
          percentage: Number(percentage)
        });
      }
    };

    const toggleBusiness = (businessId) => {
      expandedBusinesses.value[businessId] = !expandedBusinesses.value[businessId];
    };

    const getAverageSalaryTarget = (business) => {
      const targets = business.businessData?.salaryTarget || {};
      const values = Object.values(targets);
      if (values.length === 0) return 0;
      const sum = values.reduce((acc, val) => acc + val, 0);
      return (sum / values.length).toFixed(1);
    };

    const toggleClosedDays = (businessId) => {
      expandedClosedDays.value[businessId] = !expandedClosedDays.value[businessId];
    };

    const getClosedDaysCount = (business) => {
      const closedDays = business.businessData?.closedDays || {};
      return (
        (closedDays.specificDates?.length || 0) +
        (closedDays.commonDates?.length || 0) +
        (closedDays.weeklyClosures?.length || 0)
      );
    };

    const commonClosedDates = [
      { label: 'Nytårsdag (1. januar)', value: '01-01' },
      { label: 'Skærtorsdag', value: 'Skærtorsdag' },
      { label: 'Langfredag', value: 'Langfredag' },
      { label: 'Påskedag', value: 'Påskedag' },
      { label: '2. Påskedag', value: '2. påskedag' },
      { label: 'Store Bededag', value: 'Store Bededag' },
      { label: 'Kr. Himmelfartsdag', value: 'Kr. Himmelfartsdag' },
      { label: 'Pinsedag', value: 'Pinsedag' },
      { label: '2. Pinsedag', value: '2. Pinsedag' },
      { label: 'Juleaften (24. december)', value: '12-24' },
      { label: '1. Juledag (25. december)', value: '12-25' },
      { label: '2. Juledag (26. december)', value: '12-26' },
      { label: 'Nytårsaften (31. december)', value: '12-31' },
    ];

    const isCommonDateSelected = (businessId, dateValue) => {
      if (!closedDays.value[businessId]?.commonDates) {
        return false;
      }
      return closedDays.value[businessId].commonDates.includes(dateValue);
    };

    const toggleCommonDate = (businessId, dateValue) => {
      if (!closedDays.value[businessId]) {
        closedDays.value[businessId] = {};
      }
      
      if (!closedDays.value[businessId].commonDates) {
        closedDays.value[businessId].commonDates = [];
      }

      const index = closedDays.value[businessId].commonDates.indexOf(dateValue);
      if (index === -1) {
        closedDays.value[businessId].commonDates.push(dateValue);
      } else {
        closedDays.value[businessId].commonDates.splice(index, 1);
      }
    };

    const saveClosedDays = async (businessId) => {
      await store.dispatch('updateClosedDays', {
        businessId,
        closedDays: {
          commonDates: closedDays.value[businessId].commonDates || [],
          weeklyClosures: closedDays.value[businessId].weeklyClosures || [],
          specificDates: closedDays.value[businessId].specificDates || []
        }
      });
    };

    const calendarAttributes = [
      {
        dot: true,
        dates: new Date(),
        color: 'red'
      }
    ];

    const weekDays = [
      { label: 'Mandag', value: 'monday' },
      { label: 'Tirsdag', value: 'tuesday' },
      { label: 'Onsdag', value: 'wednesday' },
      { label: 'Torsdag', value: 'thursday' },
      { label: 'Fredag', value: 'friday' },
      { label: 'Lørdag', value: 'saturday' },
      { label: 'Søndag', value: 'sunday' }
    ];

    const isWeekdaySelected = (businessId, weekday) => {
      if (!closedDays.value[businessId]?.weeklyClosures) {
        return false;
      }
      return closedDays.value[businessId].weeklyClosures.includes(weekday);
    };

    const toggleWeekday = (businessId, weekday) => {
      if (!closedDays.value[businessId]) {
        closedDays.value[businessId] = {};
      }
      
      if (!closedDays.value[businessId].weeklyClosures) {
        closedDays.value[businessId].weeklyClosures = [];
      }

      const index = closedDays.value[businessId].weeklyClosures.indexOf(weekday);
      if (index === -1) {
        closedDays.value[businessId].weeklyClosures.push(weekday);
      } else {
        closedDays.value[businessId].weeklyClosures.splice(index, 1);
      }
    };

    const toggleTakeaway = (businessId) => {
      expandedTakeaway.value[businessId] = !expandedTakeaway.value[businessId];
    };

    const addOrderType = (businessId) => {
      const val = (newOrderTypeInput.value[businessId] || '').trim();
      if (!val) return;
      if (!takeawayInputs.value[businessId]) takeawayInputs.value[businessId] = [];
      if (!takeawayInputs.value[businessId].includes(val)) {
        takeawayInputs.value[businessId].push(val);
      }
      newOrderTypeInput.value[businessId] = '';
    };

    const removeOrderType = (businessId, index) => {
      takeawayInputs.value[businessId].splice(index, 1);
    };

    const saveTakeawayOrderTypes = async (businessId) => {
      await store.dispatch('updateTakeawayOrderTypes', {
        businessId,
        orderTypes: [...(takeawayInputs.value[businessId] || [])]
      });
    };

    const toggleThresholds = (businessId) => {
      expandedThresholds.value[businessId] = !expandedThresholds.value[businessId];
    };

    const updateThresholdInput = (businessId, key, event) => {
      if (!thresholdInputs.value[businessId]) thresholdInputs.value[businessId] = {};
      thresholdInputs.value[businessId][key] = Number(event.target.value);
    };

    const saveThresholds = async (businessId) => {
      await store.dispatch('updateBusynessThresholds', {
        businessId,
        thresholds: { ...thresholdInputs.value[businessId] }
      });
    };

    const isAuthorized = computed(() => {
      const role = store.state.role;
      return role === 'admin' || role === 'owner';
    });

    const isOwner = computed(() => {
      return store.state.role === 'owner';
    });

    const toggleSection = (section) => {
      expandedSections.value[section] = !expandedSections.value[section];
    };

    const organizations = computed(() => store.state.organizations || []);

    return {
      months,
      userBusinesses,
      salaryInputs,
      saveSalaryTargets,
      updateSalaryInput,
      expandedBusinesses,
      toggleBusiness,
      getAverageSalaryTarget,
      expandedClosedDays,
      closedDays,
      toggleClosedDays,
      saveClosedDays,
      getClosedDaysCount,
      calendarAttributes,
      commonClosedDates,
      isCommonDateSelected,
      toggleCommonDate,
      weekDays,
      isWeekdaySelected,
      toggleWeekday,
      isAuthorized,
      isOwner,
      expandedSections,
      toggleSection,
      organizations,
      expandedTakeaway,
      takeawayInputs,
      newOrderTypeInput,
      toggleTakeaway,
      addOrderType,
      removeOrderType,
      saveTakeawayOrderTypes,
      expandedThresholds,
      thresholdInputs,
      thresholdFields,
      toggleThresholds,
      updateThresholdInput,
      saveThresholds,
    };
  }
};
</script>

<style scoped>
.admin-page {
  padding: 2rem;
  background-color: var(--background-color);
  min-height: 100vh;
}

.page-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.header-section {
  text-align: center;
  margin-bottom: 2rem;
}

.header-section h1 {
  font-family: 'Darker Grotesque', sans-serif;
  font-size: 2.5rem;
  color: var(--primary-text-color);
  margin-bottom: 0.5rem;
}

.header-section .subtitle {
  color: var(--grey);
  font-size: 1.1rem;
  margin: 0;
}

.admin-sections {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.section-header {
  background: var(--card-background);
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header:hover {
  background: var(--card-hover-background);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-content i {
  color: var(--primary-text-color);
  font-size: 1.2rem;
  display: flex;
  align-items: center;
}

.header-content h2,
.header-content h3 {
  margin: 0;
  line-height: 1;
  display: flex;
  align-items: center;
}

.section-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
  background: var(--card-background);
  border-radius: 0 0 12px 12px;
  margin-top: -12px;
}

.section-content.is-expanded {
  max-height: 5000px;
  padding: 2rem;
  overflow: visible;
}

.business-targets {
  background: white;
  border-radius: 12px;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px var(--shadow-color);
  overflow: hidden;
  transition: all 0.3s ease;
}

.business-targets:hover {
  box-shadow: 0 4px 8px var(--shadow-color);
}

.business-header {
  padding: 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  transition: background-color 0.2s ease;
}

.business-header:hover {
  background: #f5f5f5;
}

.expand-icon {
  color: var(--primary-text-color);
  transition: transform 0.3s ease;
  font-size: 0.8rem;
}

.expand-icon.is-expanded {
  transform: rotate(180deg);
}

.business-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
  background: white;
}

.business-content.is-expanded {
  max-height: 1000px;
  padding: 1.5rem;
}

.save-button {
  background-color: var(--primary-text-color);
  color: var(--white);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 0.9rem;
}

.save-button:hover {
  background-color: var(--hover-color);
}

@media (max-width: 768px) {
  .admin-page {
    padding: 1rem;
  }
  
  .header-section h1 {
    font-size: 2rem;
  }
  
  .section-content.is-expanded {
    padding: 1rem;
  }
  
  .business-content.is-expanded {
    padding: 1rem;
  }
}

.closed-days {
  margin-top: 2rem;
}

.closed-days-picker {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.quick-select-options {
  margin-bottom: 2rem;
}

.common-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

.quick-select-btn {
  padding: 0.5rem 1rem;
  border: 1px solid var(--primary-text-color);
  border-radius: 20px;
  background: var(--card-background);
  color: var(--primary-text-color);
  cursor: pointer;
  transition: all 0.2s ease;
}

.quick-select-btn:hover {
  background: var(--background-color);
}

.quick-select-btn.selected {
  background: var(--primary-text-color);
  color: var(--white);
}

h4 {
  color: var(--primary-text-color);
  margin-bottom: 1rem;
}

.weekly-closures {
  margin-bottom: 2rem;
}

.weekday-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

@media (max-width: 768px) {
  .weekday-buttons {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 480px) {
  .weekday-buttons {
    grid-template-columns: repeat(3, 1fr);
  }
}

.admin-section {
  margin-bottom: 3rem;
}

.admin-section h2 {
  color: var(--primary-text-color);
  margin-bottom: 0rem;
  font-family: 'Montserrat', sans-serif;
  border-bottom: 2px solid var(--background-color);
  padding-bottom: 0.5rem;
}

.admin-section h3 {
  color: var(--primary-text-color);
  margin: 2rem 0 1rem;
  font-family: 'Montserrat', sans-serif;
}

.section-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.section-content.is-expanded {
  max-height: 5000px; /* Adjust based on content */
}

.expand-icon {
  color: var(--primary-text-color);
  transition: transform 0.3s ease;
}

.expand-icon.is-expanded {
  transform: rotate(180deg);
}

.header-section {
  margin-bottom: 2rem;
}

.header-section h1 {
  color: var(--primary-text-color);
  margin-bottom: 0.5rem;
}

.header-section .subtitle {
  color: var(--grey);
  font-size: 1.1rem;
}

.admin-sections {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.section-header {
  background: var(--card-background);
  border-radius: 12px;
  padding: 2.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.section-header:hover {
  background: var(--card-hover-background);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-content i {
  color: var(--primary-text-color);
  font-size: 1.2rem;
  display: flex;
  align-items: center;
}

.section-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
  background: var(--card-background);
  border-radius: 0 0 12px 12px;
  margin-top: -12px;
}

.section-content.is-expanded {
  max-height: 5000px;
  padding: 2rem;
}

.user-management-sections {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 800px;
  margin: 0 auto;
}

.add-user-section, 
.manage-users-section {
  width: 100%;
  background: var(--card-background);
  border-radius: 12px;
  overflow: hidden;
}

.section-content.is-expanded {
  max-height: 5000px;
  padding: 2rem;
}

.months-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
}

.month-input {
  flex: 1 1 calc(25% - 1rem);
  min-width: 150px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.input-group input {
  width: 80px;
  padding: 0.5rem;
  border: 1px solid var(--input-border);
  border-radius: 4px;
}

.percentage {
  color: var(--grey);
}

.chevron-icon {
  width: 24px;
  height: 24px;
  transition: transform 0.3s ease;
}

.chevron-icon.is-expanded {
  transform: rotate(180deg);
}


.salary-targets h3,
.closed-days h3 {
  color: var(--primary-text-color);
  margin-bottom: 0.5rem;
  font-family: 'Montserrat', sans-serif;
  border-bottom: 2px solid var(--background-color);
  padding-bottom: 0.5rem;
}

.vat-toggle-section {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--background-color);
}

.vat-label {
  font-size: 0.6em;
  color: var(--grey);
  font-weight: normal;
  margin-left: 0.5rem;
}

.takeaway-types {
  margin-top: 2rem;
}

.takeaway-helper {
  font-size: 0.85rem;
  color: var(--grey);
  margin-bottom: 1rem;
  line-height: 1.4;
}

.order-type-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  background: var(--primary-text-color);
  color: var(--white);
  font-size: 0.85rem;
  font-weight: 500;
}

.chip--empty {
  background: var(--light-grey);
  color: var(--grey);
}

.chip-remove {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  opacity: 0.7;
}

.chip-remove:hover {
  opacity: 1;
}

.add-order-type {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.add-order-type input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid var(--input-border);
  border-radius: 4px;
  font-size: 0.9rem;
}

.add-btn {
  padding: 0.5rem 1rem;
  background: var(--primary-text-color);
  color: var(--white);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  white-space: nowrap;
}

.add-btn:hover {
  background: var(--hover-color);
}

.thresholds-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.threshold-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.threshold-input label {
  color: var(--primary-text-color);
  font-weight: 600;
  font-size: 0.9rem;
}
</style>
